import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_CARE_SERVICE_AREA = 'care-service-area';
export const BD_CARE_SERVICE_AREA_CONDITION_ID = 'careServiceArea';
export const FilterConditionCareServiceAreaIdPluginType: FilterConditionPluginSpecification<
  number[],
  FilterConditionCareServiceAreaIdDto,
  BdCareServiceAreaCondition
> = {
  type: 'careServiceAreaIds',
  category: 'person',
  displayName: { de: 'Versorgungsgebiete' },
  sortingIndex: 3,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionCareServiceAreaIdDto,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionCareServiceAreaIdPlugin(obj.careServiceAreaIds);
  },
  fromCondition(
    jsonObj: BdCareServiceAreaCondition,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionCareServiceAreaIdPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<number[]> {
    return new FilterConditionCareServiceAreaIdPlugin([]);
  },
};

export class FilterConditionCareServiceAreaIdPlugin extends FilterConditionPluginImpl<
  number[],
  FilterConditionCareServiceAreaIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    number[],
    FilterConditionCareServiceAreaIdDto,
    BdCareServiceAreaCondition
  > = FilterConditionCareServiceAreaIdPluginType;

  constructor(private careServiceAreaIds: number[]) {
    super(FilterConditionCareServiceAreaIdPluginType.type);
  }

  clone(): FilterConditionCareServiceAreaIdPlugin {
    return new FilterConditionCareServiceAreaIdPlugin([
      ...(this.careServiceAreaIds ? this.careServiceAreaIds : []),
    ]);
  }

  override equals(other: FilterConditionCareServiceAreaIdPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.careServiceAreaIds, other.careServiceAreaIds)
    );
  }

  override validationToText(): string {
    if (this.careServiceAreaIds.length === 0) {
      return 'Mindestens ein Versorgungsgebiet auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdCareServiceAreaCondition(this.careServiceAreaIds, []);
  }

  toJSON(): FilterConditionCareServiceAreaIdDto | null {
    return {
      type: this.type,
      careServiceAreaIds: this.careServiceAreaIds,
    };
  }

  toText(): string | null {
    switch (this.careServiceAreaIds.length) {
      case 0:
        return 'keine Versorgungsgebiete';
      case 1:
        return 'ein Versorgungsgebiet';
      default:
        return `${this.careServiceAreaIds.length} Versorgungsgebiete`;
    }
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.careServiceAreaIds.length === 0) return {};
    return {
      [FILTER_RESOURCE_CARE_SERVICE_AREA]:
        this.careServiceAreaIds.map(String),
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.careServiceAreaIds.map((id) => {
      const key = String(id);
      return resolvedNames?.get(key) ?? key;
    });
    return { title, items };
  }

  toValue(): number[] {
    return this.careServiceAreaIds;
  }
}

export class BdCareServiceAreaCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(BD_CARE_SERVICE_AREA_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionCareServiceAreaIdDto {
  type: string;
  careServiceAreaIds: number[];
}
